import React, { memo, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { RootState } from "../../store";
import { useSelector } from "react-redux";

// Types for better type safety
interface FooterLink {
  label: string;
  href: string;
  icon?: React.ReactNode;
  external?: boolean;
}

interface SocialLink {
  name: string;
  icon: React.ReactNode;
  href: string;
  color: string;
}

interface FooterProps {
  /** Custom logo component */
  logo?: React.ReactNode;
  /** Custom title - defaults to translation */
  title?: string;
  /** Custom description - defaults to translation */
  description?: string;
  /** Quick links array */
  quickLinks?: FooterLink[];
  /** Support links array */
  supportLinks?: FooterLink[];
  /** Social media links */
  socialLinks?: SocialLink[];
  /** Custom copyright text - defaults to translation */
  copyrightText?: string;
  /** Whether to show newsletter section */
  showNewsletter?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom background gradient */
  backgroundGradient?: string;
}

/**
 * Professional Footer component with modern design, full responsiveness,
 * light/dark mode support using Tailwind CSS, and internationalization
 */
const Footer: React.FC<FooterProps> = memo(
  ({
    logo,
    title,
    description,
    quickLinks,
    supportLinks,
    socialLinks,
    copyrightText,
    showNewsletter = false,
    className = "",
    backgroundGradient = "bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-gray-900 dark:to-zinc-900",
  }) => {
    const { t } = useTranslation();
    const { currentLanguage } = useSelector(
      (state: RootState) => state.language
    );

    // Default logo
    const defaultLogo = useMemo(
      () => (
        <img
          src="/assets/ddc-logo.jpeg"
          alt="DDC Logo"
          className="w-24 h-24 rounded-full object-cover"
        />
      ),
      []
    );

    // Default quick links
    const defaultQuickLinks = useMemo(
      () => [
        { label: t("header.nav.home"), href: "/", icon: "🏠" },
        {
          label: t("header.nav.about"),
          href: `/${currentLanguage}/about`,
          icon: "ℹ️",
        },
        {
          label: t("header.nav.projects"),
          href: `/${currentLanguage}/projects`,
          icon: "🚀",
        },
        {
          label: t("header.nav.events"),
          href: `/${currentLanguage}/events`,
          icon: "📅",
        },
        {
          label: t("header.nav.blog"),
          href: `/${currentLanguage}/blogs`,
          icon: "📝",
        },
        {
          label: t("header.nav.businesses"),
          href: `/${currentLanguage}/businesses`,
          icon: "💼",
        },
        {
          label: t("attractions.title", "Attractions"),
          href: `/${currentLanguage}/attractions`,
          icon: "🏖️",
        },
      ],
      [t]
    );

    // Default support links
    const defaultSupportLinks = useMemo(
      () => [
        { label: t("contact.contactUs"), href: "#", icon: "📞" },
        { label: "Privacy Policy", href: "#", icon: "🔒" },
        { label: "Terms of Service", href: "#", icon: "📋" },
        { label: "Help Center", href: "#", icon: "❓" },
      ],
      [t]
    );

    // Default social links with enhanced styling
    const defaultSocialLinks = useMemo(
      () => [
        {
          name: "Facebook",
          href: "#",
          color: "hover:text-blue-600 dark:hover:text-blue-400",
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          ),
        },
        {
          name: "WhatsApp",
          href: "#",
          color: "hover:text-green-600 dark:hover:text-green-400",
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          ),
        },
        {
          name: "LinkedIn",
          href: "#",
          color: "hover:text-blue-700 dark:hover:text-blue-500",
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          ),
        },
        {
          name: "YouTube",
          href: "#",
          color: "hover:text-red-600 dark:hover:text-red-500",
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          ),
        },
      ],
      []
    );

    // Animation variants
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.2,
        },
      },
    };

    const itemVariants = {
      hidden: { y: 30, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          duration: 0.6,
        },
      },
    };

    const linkHoverVariants = {
      hover: {
        x: 5,
        transition: { duration: 0.2 },
      },
    };

    return (
      <motion.footer
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className={`${backgroundGradient} text-gray-900 dark:text-white relative overflow-hidden ${className}`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10">
          {/* Main Footer Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {/* Brand Section */}
              <motion.div variants={itemVariants} className="lg:col-span-1">
                <div className="flex items-center gap-3 mb-4">
                  {logo || defaultLogo}
                  <h3 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-stone-600 dark:from-emerald-400 dark:to-stone-400 bg-clip-text text-transparent">
                    {title || t("footer.title")}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed mb-6">
                  {description || t("footer.description")}
                </p>

                {/* Newsletter Section (Optional) */}
                {showNewsletter && (
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t("footer.newsletterTitle")}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 text-base">
                      {t("footer.newsletterDescription")}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder={t("newsletter.placeholder")}
                        className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <button className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 rounded-lg text-white font-medium transition-all duration-200 text-sm">
                        {t("newsletter.buttonText")}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Quick Links */}
              <motion.div variants={itemVariants}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {t("footer.quickLinks")}
                </h4>
                <ul className="space-y-3">
                  {(quickLinks || defaultQuickLinks).map((link) => (
                    <motion.li
                      key={link.label}
                      variants={linkHoverVariants}
                      whileHover="hover"
                    >
                      <Link
                        to={link.href}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 text-base group"
                      >
                        {link.icon && (
                          <span className="text-base group-hover:scale-110 transition-transform duration-200">
                            {link.icon}
                          </span>
                        )}
                        <span>{link.label}</span>
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Support Links */}
              <motion.div variants={itemVariants}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {t("footer.support")}
                </h4>
                <ul className="space-y-3">
                  {(supportLinks || defaultSupportLinks).map((link) => (
                    <motion.li
                      key={link.label}
                      variants={linkHoverVariants}
                      whileHover="hover"
                    >
                      <a
                        href={link.href}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 text-sm group"
                      >
                        {link.icon && (
                          <span className="text-base group-hover:scale-110 transition-transform duration-200">
                            {link.icon}
                          </span>
                        )}
                        <span>{link.label}</span>
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Social Links & Contact */}
              <motion.div variants={itemVariants}>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {t("footer.followUs")}
                </h4>

                {/* Social Icons */}
                <div className="flex gap-4 mb-6">
                  {(socialLinks || defaultSocialLinks).map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      whileHover={{ scale: 1.2, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                      className={`text-gray-500 dark:text-gray-400 ${social.color} transition-all duration-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10`}
                      aria-label={social.name}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
                    <svg
                      className="w-4 h-4 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <span>contact@ddc.org</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
                    <svg
                      className="w-4 h-4 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>+1 (555) 123-4567</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Bottom Bar */}
          <motion.div
            variants={itemVariants}
            className="border-t border-gray-200 dark:border-gray-800"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {copyrightText || t("footer.copyright")}
                </p>

                {/* Back to Top Button */}
                <motion.button
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 text-sm group"
                >
                  <span>{t("footer.backToTop")}</span>
                  <svg
                    className="w-4 h-4 group-hover:-translate-y-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 10l7-7m0 0l7 7m-7-7v18"
                    />
                  </svg>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.footer>
    );
  }
);

// Display name for debugging
Footer.displayName = "Footer";

export default Footer;
